import { createApp } from 'vue'

import App from './App.vue'
import router from './router'
import { setAuthTokenResolver, setTokenRefreshAttempter, setUnauthorizedHandler } from './api/request'
import ToastContainer from './components/ToastContainer.vue'
import BaseButton from './components/BaseButton.vue'
import BaseInput from './components/BaseInput.vue'
import DropdownSelect from './components/DropdownSelect.vue'
import BaseCheckbox from './components/BaseCheckbox.vue'
import BaseRadio from './components/BaseRadio.vue'
import BasePagination from './components/BasePagination.vue'
import ConfirmDialog from './components/ConfirmDialog.vue'
import SearchInput from './components/SearchInput.vue'
import BaseBadge from './components/BaseBadge.vue'
import { pinia } from './stores/pinia'
import { useUserStore } from './stores/user'
// 预加载全部租户级 Store，使其自我登记进 _tenantScopedRegistry（切换租户/登出时统一清空）。
import './stores/tenantScopedStores'
import './styles/index.scss'

const app = createApp(App)
const userStore = useUserStore(pinia)

setAuthTokenResolver(() => userStore.token)
setTokenRefreshAttempter(() => userStore.refreshAccessToken({ skipUnauthorizedHandler: true }))
setUnauthorizedHandler((error) => {
  // 租户被暂停/到期：跳到专用提示页（不调 logout，保留 userInfo/页面状态）
  if (error.code === 'TENANT_SUSPENDED' || error.code === 'TENANT_EXPIRED') {
    const reason = error.code === 'TENANT_SUSPENDED' ? 'paused' : 'expired'
    const currentRoute = router.currentRoute.value
    if (currentRoute.name === 'TenantPaused') {
      return
    }
    void router.replace({ name: 'TenantPaused', query: { reason } })
    return
  }

  // 登录凭证错误由登录页自行提示，不按会话失效处理
  if (error.code === 'INVALID_CREDENTIALS') {
    return
  }

  // 其它未授权（TOKEN_EXPIRED 等）：登出后跳登录页
  userStore.logout()

  const currentRoute = router.currentRoute.value
  if (
    currentRoute.name === 'Login' ||
    currentRoute.name === 'SchoolLogin' ||
    currentRoute.name === 'OpsLogin'
  ) {
    return
  }

  const targetName = currentRoute.path.startsWith('/ops') ? 'OpsLogin' : 'Login'
  void router.replace({
    name: targetName,
    query: {
      redirect: currentRoute.fullPath,
      reason: 'expired'
    }
  })
})

app.use(pinia)
app.use(router)
app.component('ToastContainer', ToastContainer)
app.component('BaseButton', BaseButton)
app.component('BaseInput', BaseInput)
app.component('DropdownSelect', DropdownSelect)
app.component('BaseCheckbox', BaseCheckbox)
app.component('BaseRadio', BaseRadio)
app.component('BasePagination', BasePagination)
app.component('ConfirmDialog', ConfirmDialog)
app.component('SearchInput', SearchInput)
app.component('BaseBadge', BaseBadge)

async function bootstrap(): Promise<void> {
  await userStore.hydrateSession()
  await router.isReady()
  app.mount('#app')
}

void bootstrap()
